import React from 'react';
import { FaInfoCircle } from 'react-icons/fa';

import { Tooltip } from '@oncolens/oncolens-ui/lib/components/FormControls/Tooltip';
import { Checkbox } from '@/components/v2/Form/Checkbox';
import { Input } from '@/components/v2/Form/Input';

export interface ConferenceVirtualSettingsValues {
  infoPanelEnabled: boolean;
  audioRecording: boolean;
  videoRecording: boolean;
  joinUrl: string;
  startUrl: string;
}

function isValidUrl(value: string): boolean {
  if (!value) return true;
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

export function hasValidUrls(values: ConferenceVirtualSettingsValues): boolean {
  return isValidUrl(values.joinUrl) && isValidUrl(values.startUrl);
}

export function isConferenceVirtualSettingsDirty(
  current: ConferenceVirtualSettingsValues,
  loaded: ConferenceVirtualSettingsValues,
): boolean {
  return (
    current.infoPanelEnabled !== loaded.infoPanelEnabled ||
    current.audioRecording !== loaded.audioRecording ||
    current.videoRecording !== loaded.videoRecording ||
    current.joinUrl !== loaded.joinUrl ||
    current.startUrl !== loaded.startUrl
  );
}

export interface OrgVirtualSettingsDefaults {
  infoPanelEnabled: boolean;
  audioRecording: boolean;
}

interface ConferenceVirtualSettingsProps {
  values: ConferenceVirtualSettingsValues;
  onChange: (values: ConferenceVirtualSettingsValues) => void;
  disabled: boolean;
  orgDefaults?: OrgVirtualSettingsDefaults;
}

const OverrideTooltip = ({ orgDefault, field }: { orgDefault: boolean; field: string }) => (
  <Tooltip
    testSelector={`vtb-${field}-override-tooltip`}
    text={
      <span>
        Organization default: <strong>{orgDefault ? 'On' : 'Off'}</strong>
        <br />
        <span className="text-amber-400">Overridden at conference level</span>
      </span>
    }
    icon={<FaInfoCircle className="ml-1.5 cursor-help text-amber-400" />}
  />
);

export const ConferenceVirtualSettings = ({
  values,
  onChange,
  disabled,
  orgDefaults,
}: ConferenceVirtualSettingsProps) => {
  const update = (partial: Partial<ConferenceVirtualSettingsValues>) =>
    onChange({ ...values, ...partial });

  return (
    <div
      data-cy="vtb-settings-section"
      className="bg-white shadow-sm border rounded-lg w-full mt-6">
      <div className="border-b-[#E4E4E7] border-b">
        <div className="flex items-center px-6 py-2">
          <p className="font-bold text-sm text-zinc-800">Virtual conference settings</p>
          <Tooltip
            testSelector="vtb-settings-info-tooltip"
            text="These settings are available only when the Virtual Conference license is enabled. By default, they inherit organization-level settings unless edited."
            icon={<FaInfoCircle className="ml-1.5 text-blue-400 cursor-help" />}
          />
        </div>
      </div>
      <div className="py-8 px-6 flex flex-col gap-5">
        <div className="flex items-center">
          <Checkbox
            data-cy="vtb-info-panel-checkbox"
            isSelected={values.infoPanelEnabled}
            onChange={(val) => update({ infoPanelEnabled: val })}
            isDisabled={disabled}>
            Allow admins to see the meeting info panel
          </Checkbox>
          {!disabled && orgDefaults && values.infoPanelEnabled !== orgDefaults.infoPanelEnabled && (
            <OverrideTooltip orgDefault={orgDefaults.infoPanelEnabled} field="info-panel" />
          )}
        </div>
        <div className="flex gap-6">
          <div className="flex items-center">
            <Checkbox
              data-cy="vtb-audio-recording-checkbox"
              isSelected={values.audioRecording}
              onChange={(val) => update({ audioRecording: val })}
              isDisabled={disabled}>
              Audio recording
            </Checkbox>
            {!disabled && orgDefaults && values.audioRecording !== orgDefaults.audioRecording && (
              <OverrideTooltip orgDefault={orgDefaults.audioRecording} field="audio-recording" />
            )}
          </div>
          <Checkbox
            data-cy="vtb-video-recording-checkbox"
            isSelected={values.videoRecording}
            onChange={(val) => update({ videoRecording: val })}
            isDisabled={disabled}>
            Video recording
          </Checkbox>
        </div>
        <div className="flex gap-6 w-full">
          <div className="w-full max-w-xs">
            <label className="text-xs text-zinc-800 font-normal">Join URL</label>
            <Input
              data-cy="vtb-join-url-input"
              value={values.joinUrl}
              onChange={(e) => update({ joinUrl: e.target.value })}
              disabled={disabled}
              placeholder="https://"
            />
            {!isValidUrl(values.joinUrl) && (
              <p
                data-cy="vtb-join-url-error"
                className="text-[0.8rem] font-medium text-destructive mt-1">
                Must be a valid URL
              </p>
            )}
          </div>
          <div className="w-full max-w-xs">
            <label className="text-xs text-zinc-800 font-normal">Start URL</label>
            <Input
              data-cy="vtb-start-url-input"
              value={values.startUrl}
              onChange={(e) => update({ startUrl: e.target.value })}
              disabled={disabled}
              placeholder="https://"
            />
            {!isValidUrl(values.startUrl) && (
              <p
                data-cy="vtb-start-url-error"
                className="text-[0.8rem] font-medium text-destructive mt-1">
                Must be a valid URL
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
